/**
 * The playground's one road to the API: any operation in the spec, by id,
 * over REST or as an MCP `tools/call`.
 *
 * Every call becomes a CallRecord: what was asked, by which persona, from
 * which source, its REST/curl/MCP twins, and the answer. `call` never
 * throws; a host that cannot be reached answers status 0, and arguments no
 * request can be made of answer 400 "Bad Arguments" unsent. Every write the
 * spec lets carry an Idempotency-Key gets `pg-<tab>-<n>`, and is retried once
 * with the same key when nothing came back (status 0) or the relay road was
 * busy (503 "Road Busy"), so a retry can never do the act twice.
 *
 * A `w` that is a persona's World key (`B:mill`) goes as that persona, with
 * the World's own name: the record says persona B and `w: 'mill'`. A call
 * made with `pin` (every scenario step) keeps the persona it was given, and
 * one whose `w` names another persona's World is refused unsent (400
 * "Persona Pinned"), so a use case never acts as a key it didn't bind.
 *
 * Listeners (the Wire, the recorder) receive a scrubbed copy of each record:
 * no key-shaped string, and "(shown once)" where the answer held a secret.
 * The caller alone gets the answer as it came, so a new key's secret can be
 * shown once and then let go. Calls the Director makes on the server arrive
 * through `inject` and are heard the same way.
 */

import type { Answered, Args, Catalogue, CallRecord, Client, CreateClient, Json, Operation, Persona, SessionApi, Twins, Unsub } from '../contracts';

import { keyHint, mcpOf, redact, redactText, restOf, twinsOf } from '../spec/twins';
import { isPersonaKey, worldRef } from '../worlds';
import { isObj } from '../model/json';

/** What the API refuses a sandbox key, by the spec's tags (cloud/src/core/sandbox.ts SANDBOX_REFUSES). */
const SANDBOX_REFUSED_TAGS: ReadonlySet<string> = new Set(['keys', 'webhooks', 'grants', 'billing']);

/**
 * A persona's World key in `w` names its persona and its World: the key's persona speaks for it.
 * Pinned, the call's persona stands, and a key naming another persona's World is `borrowed`: the
 * call is refused, never sent with that persona's key.
 */
export const unkey = (args: Args, persona?: Persona, pin = false): { args: Args; persona: Persona; borrowed?: Persona } => {
  if (typeof args.w === 'string' && isPersonaKey(args.w)) {
    const ref = worldRef(args.w);
    if (pin && ref.persona !== (persona ?? 'A')) return { args, persona: persona ?? 'A', borrowed: ref.persona };
    return { args: { ...args, w: ref.w }, persona: ref.persona };
  }
  return { args, persona: persona ?? 'A' };
};

export interface ClientDeps {
  readonly session: SessionApi;
  readonly catalogue: () => Catalogue;
  readonly fetchFn?: typeof fetch;
  readonly now?: () => number;
  /** How long to wait before the one retry of a write. */
  readonly retryDelayMs?: number;
  readonly sleep?: (ms: number) => Promise<void>;
}

type Phase = 'sent' | 'answered';

const HEAD = /^[0-9a-f]{32}$/;

const problem = (status: number, title: string, detail: string): Json => ({ type: 'about:blank', title, status, detail });

/** RateLimit headers as numbers: the per-header form, or the structured `ratelimit` field. */
export const rateOf = (headers: Headers): Answered['rate'] => {
  const n = (v: string | null | undefined) => (v === null || v === undefined || v.trim() === '' ? NaN : Number(v));
  let limit = n(headers.get('ratelimit-limit'));
  let remaining = n(headers.get('ratelimit-remaining'));
  let reset = n(headers.get('ratelimit-reset'));
  const structured = headers.get('ratelimit');
  if (structured && (Number.isNaN(limit) || Number.isNaN(remaining))) {
    const field = (k: string) => n(new RegExp(`(?:^|[;,\\s])${k}=(\\d+)`).exec(structured)?.[1]);
    limit = Number.isNaN(limit) ? field('limit') : limit;
    remaining = Number.isNaN(remaining) ? field('remaining') : remaining;
    reset = Number.isNaN(reset) ? field('reset') : reset;
  }
  if (Number.isNaN(limit) || Number.isNaN(remaining)) return null;
  return { limit, remaining, reset: Number.isNaN(reset) ? 0 : reset };
};

const parse = (text: string): Json => {
  if (!text) return null;
  try { return JSON.parse(text) as Json; } catch { return text; }
};

const headOf = (header: string | null, body: Json): string | null => {
  if (header && HEAD.test(header)) return header;
  return isObj(body) && typeof body.head === 'string' && HEAD.test(body.head) ? body.head : null;
};

/** A copy of an answer fit for the Wire: secrets gone. */
const scrubAnswer = (a: Answered): Answered => ({ ...a, body: redact(a.body) });

const isWrite = (op: Operation): boolean => op.method !== 'GET';

/** Twins for a call no request could be made of. */
const noTwins = (): Twins => ({ rest: { method: '?', url: '', headers: {} }, curl: '', mcp: null });

export const createClient: CreateClient = (deps) => makeClient(deps);

export function makeClient(deps: ClientDeps): Client {
  const fetchFn = deps.fetchFn ?? ((input, init) => fetch(input, init));
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const retryDelay = deps.retryDelayMs ?? 400;
  const listeners = new Set<(record: CallRecord, phase: Phase) => void>();
  /** The copy each record's listeners hold, so both phases reach the same object. */
  const shown = new WeakMap<CallRecord, CallRecord>();
  let calls = 0;
  let idem = 0;
  let rpc = 0;

  const emit = (record: CallRecord, phase: Phase) => {
    let copy = shown.get(record);
    if (!copy) {
      copy = { ...record, args: redact(record.args as Json) as Args, twins: record.twins, answer: null };
      shown.set(record, copy);
    }
    copy.answer = record.answer ? scrubAnswer(record.answer) : null;
    for (const fn of [...listeners]) {
      try { fn(copy, phase); } catch { /* a listener's fault stays its own */ }
    }
  };

  const keyFor = (op: Operation, persona: Persona): string | null => {
    if (op.security === 'operator') return deps.session.operatorKey();
    if (op.security === 'key') return deps.session.key(persona);
    return null;
  };

  const twinsFor = (op: Operation, args: Args, persona: Persona, id: number): Twins =>
    twinsOf(op, args, { apiBase: deps.session.apiBase, key: keyFor(op, persona), persona, id });

  /** Arguments no request can be made of: answered here, as a 400 that says why, and never sent. */
  const badArgs = (error: unknown, key: string | null, ms: number): Answered => {
    let why = String(error);
    // Some runtimes quote the offending header, key and all.
    if (key) why = why.split(key).join(keyHint(key));
    return { status: 400, body: problem(400, 'Bad Arguments', `could not make a request of these arguments: ${redactText(why)}`), head: null, ms, requestId: null, rate: null };
  };

  /** One exchange with the API. Never throws. */
  const exchange = async (op: Operation, args: Args, key: string | null, via: 'rest' | 'mcp', id: number, signal?: AbortSignal): Promise<Answered> => {
    const started = now();
    const auth: Record<string, string> = key ? { authorization: `Bearer ${key}` } : {};
    let url: string;
    let init: RequestInit;
    try {
      if (via === 'mcp') {
        url = `${deps.session.apiBase}/v1/mcp`;
        init = { method: 'POST', headers: { 'content-type': 'application/json', ...auth }, body: JSON.stringify(mcpOf(op, args, id)), signal };
      } else {
        const rest = restOf(op, args, deps.session.apiBase);
        url = rest.url;
        init = { method: rest.method, headers: { ...rest.headers, ...auth }, body: rest.body === undefined ? undefined : JSON.stringify(rest.body), signal };
      }
      // A header fetch would refuse (a value outside Latin-1) is the arguments' fault, not the host's.
      new Headers(init.headers);
    } catch (error) {
      return badArgs(error, key, now() - started);
    }
    let response: Response;
    try {
      response = await fetchFn(url, init);
    } catch (error) {
      const aborted = signal?.aborted === true || (error instanceof Error && error.name === 'AbortError');
      // A browser reports a CORS refusal exactly like a dead host: no response at all.
      const detail = aborted
        ? 'the call was stopped before an answer came'
        : `could not reach ${deps.session.apiBase} (${redactText(String(error))}); check the address, and that this page's origin is in the API's CONSOLE_ORIGINS`;
      return { status: 0, body: problem(0, aborted ? 'Aborted' : 'Unreachable', detail), head: null, ms: now() - started, requestId: null, rate: null };
    }
    const raw = parse(await response.text().catch(() => ''));
    let status = response.status;
    let body = raw;
    if (via === 'mcp' && isObj(raw)) {
      const result = raw.result;
      if (isObj(result) && isObj(result.structuredContent) && typeof result.structuredContent.status === 'number') {
        status = result.structuredContent.status;
        body = result.structuredContent.body ?? null;
      } else if (isObj(raw.error)) {
        // The tool call could not be made at all: the MCP layer's own 400.
        status = 400;
        body = { ...(problem(400, 'Invalid Tool Call', typeof raw.error.message === 'string' ? raw.error.message : 'the MCP server refused the call') as Record<string, Json>), rpc: raw.error };
      }
    }
    return {
      status, body, head: headOf(response.headers.get('x-journal-head'), body), ms: now() - started,
      requestId: response.headers.get('x-request-id'), rate: via === 'rest' ? rateOf(response.headers) : null,
    };
  };

  return {
    async call(opId, asked, opts = {}) {
      const { args, persona, borrowed } = unkey(asked, opts.persona, opts.pin === true);
      const source = opts.source ?? { kind: 'auto' };
      const op = deps.catalogue().byId(opId);
      const n = ++calls;
      const id = `${deps.session.tabId()}-${n}`;
      /** A call answered here, never sent. */
      const unsent = (answer: Answered): CallRecord => {
        const record: CallRecord = {
          id, op: opId, args, persona, source, origin: opts.origin ?? null, via: 'rest',
          twins: noTwins(), sentAt: now(), answer: null,
        };
        emit(record, 'sent');
        record.answer = answer;
        emit(record, 'answered');
        return record;
      };
      if (borrowed) {
        return unsent({
          status: 400, body: problem(400, 'Persona Pinned', `this call acts as persona ${persona}; "${String(asked.w)}" is persona ${borrowed}'s World, and a pinned call never borrows another persona's key`),
          head: null, ms: 0, requestId: null, rate: null,
        });
      }
      if (!op) {
        return unsent({ status: 0, body: problem(0, 'Unknown Operation', `this build's spec has no operation "${opId}"; reload the spec`), head: null, ms: 0, requestId: null, rate: null });
      }
      // A sandbox has no keys, webhooks, grants or billing (the API answers 403): answered here, so nothing is sent to be refused.
      if (persona === 'A' && deps.session.sandbox() && op.security === 'key' && op.tags.some((t) => SANDBOX_REFUSED_TAGS.has(t))) {
        return unsent({
          status: 403, body: problem(403, 'Forbidden', 'a sandbox has no keys, webhooks, grants or billing of its own; sign up for a workspace of your own'),
          head: null, ms: 0, requestId: null, rate: null,
        });
      }
      let sendArgs = args;
      if (op.idempotent && isWrite(op) && args['Idempotency-Key'] === undefined) {
        sendArgs = { ...args, 'Idempotency-Key': `pg-${deps.session.tabId()}-${++idem}` };
      }
      const via = opts.via === 'mcp' && op.mcp ? 'mcp' : 'rest';
      const rpcId = ++rpc;
      const key = keyFor(op, persona);
      let twins: Twins;
      let unfit: Answered | null = null;
      try {
        twins = twinsFor(op, sendArgs, persona, rpcId);
      } catch (error) {
        twins = noTwins();
        unfit = badArgs(error, key, 0);
      }
      const record: CallRecord = {
        id, op: opId, args: sendArgs, persona, source, origin: opts.origin ?? null, via, twins, sentAt: now(), answer: null,
      };
      emit(record, 'sent');
      if (unfit) {
        record.answer = unfit;
        emit(record, 'answered');
        return record;
      }
      let answer = await exchange(op, sendArgs, key, via, rpcId, opts.signal);
      const busy = answer.status === 503 && isObj(answer.body) && answer.body.title === 'Road Busy';
      if ((answer.status === 0 || busy) && isWrite(op) && sendArgs['Idempotency-Key'] !== undefined && !opts.signal?.aborted
        && !(isObj(answer.body) && answer.body.title === 'Aborted')) {
        await sleep(retryDelay);
        if (!opts.signal?.aborted) answer = await exchange(op, sendArgs, key, via, rpcId, opts.signal);
      }
      if (op.security === 'operator' && key) deps.session.touchOperator();
      record.answer = answer;
      emit(record, 'answered');
      return record;
    },

    twins(opId, asked, asPersona) {
      const op = deps.catalogue().byId(opId);
      if (!op) return noTwins();
      const { args, persona } = unkey(asked, asPersona);
      try { return twinsFor(op, args, persona, rpc + 1); } catch { return noTwins(); }
    },

    inject(record, phase) {
      emit(record, phase);
    },

    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
}
